import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/Card';
import Icon from '../../components/AppIcon';
import { regularizationService, getErrorMessage } from '../../services/apiService';

/**
 * Vendor Regularization Form Entry Point
 * 
 * This component fetches regularization data and redirects to the registration form
 * with regularization mode enabled. The registration form handles all the UI/UX.
 */
const VendorRegularizationForm = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing regularization token');
      setLoading(false);
      return;
    }
    fetchAndRedirect();
  }, [token]);

  const fetchAndRedirect = async () => {
    try {
      setLoading(true);
      const response = await regularizationService.getRequestByToken(token);
      
      if (!response.success) {
        throw new Error('Failed to load regularization request');
      }

      const { request, original_data } = response.data;

      // Check status
      if (request.status === 'VERIFIED') {
        setError('This regularization link has already been completed.');
        setLoading(false);
        return;
      }
      
      if (request.status === 'EXPIRED') {
        setError('This regularization link has expired. Please contact the administrator.');
        setLoading(false);
        return;
      }

      // Store regularization data in sessionStorage for the registration form to pick up
      sessionStorage.setItem('regularization_mode', 'true');
      sessionStorage.setItem('regularization_token', token);
      sessionStorage.setItem('regularization_data', JSON.stringify(original_data));
      sessionStorage.setItem('regularization_request', JSON.stringify(request));

      // Redirect to registration form with regularization mode
      navigate(`/public-vendor-registration-form?mode=regularization&token=${token}`);
      
    } catch (err) {
      const errorMsg = getErrorMessage(err) || 'Failed to load regularization data';
      setError(errorMsg);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading regularization form...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center">
            <Icon name="AlertCircle" size={48} className="text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Error</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <p className="text-sm text-gray-500">
              Please contact the administrator if you believe this is an error.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null; // Should redirect, but show loading if redirect fails
};

export default VendorRegularizationForm;
